import { VFC, useState, useId, useEffect, useContext } from 'react';
import { useForm } from 'react-hook-form';
import ReactModal from 'react-modal';
import LinkToAuthorizedRoute from '@/components/LinkToAuthorizedRoute';
import useGetStaticSiteUrl from '@/hooks/useGetStaticSiteUrl';
import updateProfile from '@/apis/aniftyBackend/account/updateProfile';
import { useTranslation } from 'next-i18next';
import { User } from '@/apis/aniftyBackend/users/interface';
import TimesIcon from '@/icons/times-white.svg';
import { captureException } from '@/utils/Sentry';
import routes from '@/config/routes';
import styles from './EmailSignupModal.module.scss';
import useAccessToken from '@/hooks/useAccessToken';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';

type Props = {
  user: User;
};

type FormSchema = {
  checked: boolean;
  email: string;
};

const validationSchema = yup.object().shape({
  checked: yup.bool().isTrue(),
  email: yup.string().email().nullable().optional(),
});

const EmailSignupModal: VFC<Props> = ({ user }: Props) => {
  const { t } = useTranslation();
  const getStaticUrl = useGetStaticSiteUrl();
  const [showModal, setShowModal] = useState(true);
  const id = useId();
  const router = useRouter();

  useEffect(() => {
    sessionStorage.setItem("emailModalSeen", "true");
  }, []);

  const handleCloseModal = async () => {
    setShowModal(false);
  };
  const { accessToken, requestSignature } = useAccessToken();
  const [error, setError] = useState<string | undefined>();
  const [disableEmailSignup, setDisableEmailSignup] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormSchema>({
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = async (data: FormSchema) => {
    setDisableEmailSignup(true);
    setError(undefined);
    try {
      if (!accessToken) {
        await requestSignature();
      }
      await updateProfile(user.userID, data);
      handleCloseModal();
    } catch (err) {
      captureException(err);
      setDisableEmailSignup(false);
      setError('Something went wrong!');
    }
  };

  return (
    <ReactModal isOpen={showModal} className={styles.modal}>
      <div className={styles.headerContainer}>
        <div className={styles.closeIcon}>
          <TimesIcon height={24} width={24} onClick={handleCloseModal} />
        </div>
        <h1 className={styles.header}>{t('emailSignup.header')}</h1>
      </div>
      <div className={styles.contentContainer}>
        <p>{t('emailSignup.content')}</p>
        <ul>
          <li>{t('emailSignup.notificationActions1')}</li>
          <li>{t('emailSignup.notificationActions2')}</li>
          <li>{t('emailSignup.notificationActions3')}</li>
        </ul>
        <p>
          {t('emailSignup.emailSetting')}
          {router.locale == 'ja' ? (
            <>
              「
              <LinkToAuthorizedRoute href={routes.accountSettings}>
                <span className={styles.link}>
                  {t('emailSignup.editProfile')}
                </span>
              </LinkToAuthorizedRoute>
              」
            </>
          ) : (
            <LinkToAuthorizedRoute href={routes.accountSettings}>
              <span className={styles.link}>
                {t('emailSignup.editProfile')}
              </span>
            </LinkToAuthorizedRoute>
          )}

          {t('emailSignup.emailSetting2')}
        </p>
        <form className={styles.contentForm} onSubmit={handleSubmit(onSubmit)}>
          <input
            type="checkbox"
            name="acceptCheckbox"
            id={`${id}accept-checkbox`}
            className={styles.checkBox}
            required
            {...register('checked')}
            onInvalid={(e) => {
              (e.target as HTMLInputElement).setCustomValidity(
                t('emailSignup.checkboxValidation')
              );
              // Need to set custom validity to empty or it will not allow for resubmission
              (e.target as HTMLInputElement).setCustomValidity('');
            }}
          />
          <label htmlFor={`${id}accept-checkbox`}>
            {router.locale == 'ja' ? (
              <>
                {t('emailSignup.privacyPolicy')} 「
                <a
                  href={getStaticUrl('privacy')}
                  className={styles.link}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('emailSignup.privacyPolicyLink')}
                </a>
                」 「
                <a
                  href={getStaticUrl('policy')}
                  className={styles.link}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('emailSignup.personalInfoLink')}
                </a>
                」{t('emailSignup.privacyPolicy2')}
              </>
            ) : (
              <>
                {t('emailSignup.privacyPolicy')}{' '}
                <a
                  href={getStaticUrl('privacy')}
                  className={styles.link}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('emailSignup.privacyPolicyLink')}
                </a>
                {t('emailSignup.privacyPolicy2')}
                <a
                  href={getStaticUrl('policy')}
                  className={styles.link}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('emailSignup.personalInfoLink')}
                </a>
              </>
            )}
          </label>
          <div className={styles.emailContainer}>
            <input
              type="email"
              placeholder={t('emailSignup.placeholder')}
              className={styles.emailInput}
              {...register('email', {
                required: 'required',
              })}
            />
            <button className={styles.registerBtn} type="submit" disabled={disableEmailSignup}>
              {t('emailSignup.registerButton')}
            </button>
          </div>
          <span className={styles.errorText} role="alert">
            {errors.email && errors.email.message}
            {error}
          </span>
        </form>
      </div>
    </ReactModal>
  );
};

export default EmailSignupModal;
